import { lazy, Suspense, useEffect, useState } from 'react';
import { setPagesSpace } from '../api/pages';
import { useAuth } from '../auth/AuthContext';
import { PlatformShell } from '../shell/PlatformShell';
import { AFFILMASTER_MENU, type MenuItem, type SubItem } from '../shell/platform-menu';
import { AffilmasterAffiliatesPage } from './AffilmasterAffiliatesPage';
import { AffilmasterAffiliatesPrehledyPage } from './AffilmasterAffiliatesPrehledyPage';
import { AffilmasterKlientiPage, hintKlientSection, klientSlug } from './AffilmasterKlientiPage';
import { AffilmasterKlientiPrehledyPage } from './AffilmasterKlientiPrehledyPage';
import { AffilmasterMailingSegmentyPage } from './AffilmasterMailingSegmentyPage';
import { MailingKampanePage } from '../mailing/MailingKampanePage';
import { MailingSablonyPage } from '../mailing/MailingSablonyPage';
import { MailingDashboardPage } from '../mailing/MailingDashboardPage';
import { SettingsPage } from '../settings/SettingsPage';
import { TasksPage } from '../tasks/TasksPage';
import { CrmLeadyPage } from '../crm/CrmLeadyPage';
import { CrmPipelinePage } from '../crm/CrmPipelinePage';
import { CrmFormularPage } from '../crm/CrmFormularPage';
import { CronyPage } from '../crony/CronyPage';
import { AffilmasterDashboardPage } from './AffilmasterDashboardPage';
import { AffilmasterBankaPage } from './AffilmasterBankaPage';
import { AffilmasterUzaverkaPage } from './AffilmasterUzaverkaPage';
import { AffilmasterSocialsTestPage } from './AffilmasterSocialsTestPage';
import { AffilmasterFinanceDashboardPage } from './AffilmasterFinanceDashboardPage';
import { AffilmasterPrijmyPage } from './AffilmasterPrijmyPage';
import { VyuctovaniKlientiPage, type VyuctovaniKlientiConfig } from '../settlement/VyuctovaniKlientiPage';
import { HelpdeskPage } from '../helpdesk/HelpdeskPage';
import { computeAllowedModuleKeys } from '../shell/allowed-module-keys';
import { useScrollRestore } from '../shell/useScrollRestore';

const StrankyPromoPage = lazy(() => import('./stranky/StrankyPromoPage').then((m) => ({ default: m.StrankyPromoPage })));
const StrankyPodporaPage = lazy(() => import('./stranky/StrankyPodporaPage').then((m) => ({ default: m.StrankyPodporaPage })));
const StrankyB2BPage = lazy(() => import('./stranky/StrankyB2BPage').then((m) => ({ default: m.StrankyB2BPage })));
const StrankyVizitkyPage = lazy(() => import('./stranky/StrankyVizitkyPage').then((m) => ({ default: m.StrankyVizitkyPage })));
const AM_VYUCTOVANI: VyuctovaniKlientiConfig = { space: 'affilmaster', eyebrow: 'AFFILMASTER', manageKey: 'affilmaster:manage', currencies: ['CZK', 'EUR'] };

const EsignPage = lazy(() => import('../esign/EsignPage').then((m) => ({ default: m.EsignPage })));

// Backend ACL is the source of truth, no affilmaster access still yields 403
// from /api/v1/affilmaster/*; frontend routing here is not a security boundary.
type Route = { moduleId: string; childId?: string; detail?: string };
// Cross-space kind ('helpdesk') is stored under moduleId='helpdesk' with no
// menu match; the shell renders HelpdeskPage directly and PlatformShell shows
// the standalone helpdesk button in the header.

// Pozn.: 'affiliates-seznam' a 'klienti-seznam' mají vlastní větve (detail
// deep-link props), proto nejsou v této bez-prop mapě.
const AM_MAILING_COUNTRIES = ['CZ', 'SK'] as const;
const DEFAULT_ESIGN_HEADER = {
  title: 'eSign',
  description: 'Podpis smluv a dokumentů na dálku',
};

const BUILT_PAGES: Record<string, () => JSX.Element> = {
  'affiliates-prehledy': AffilmasterAffiliatesPrehledyPage,
  'am-banka': AffilmasterBankaPage,
  'am-rozvaha': AffilmasterUzaverkaPage,
  'am-finance': AffilmasterFinanceDashboardPage,
  'am-prijmy': AffilmasterPrijmyPage,
  'am-mailing-segmenty': AffilmasterMailingSegmentyPage,
  'am-mailing': () => <MailingDashboardPage space="affilmaster" eyebrow="MAILING" />,
  'am-mailing-kampane': () => <MailingKampanePage space="affilmaster" eyebrow="MAILING" countries={AM_MAILING_COUNTRIES} />,
  'am-mailing-sablony': () => <MailingSablonyPage space="affilmaster" eyebrow="MAILING" countries={AM_MAILING_COUNTRIES} />,
};

function routeToPath(route: Route): string {
  if (route.moduleId === 'settings') return '/nastaveni';
  if (route.moduleId === 'crony') return '/crony';
  if (route.moduleId === 'helpdesk') return '/helpdesk';
  if (route.moduleId === 'socials-test') return '/affilmaster/socials/test';
  if (route.moduleId === 'dashboard') return '/affilmaster';
  if (route.childId) {
    const mod = AFFILMASTER_MENU.find((m) => m.id === route.moduleId);
    const child = mod?.children?.find((c) => c.id === route.childId);
    if (child?.path) return route.detail ? `${child.path}/${route.detail}` : child.path;
  }
  return '/affilmaster';
}

function pathToRoute(pathname: string): Route {
  if (pathname === '/nastaveni') return { moduleId: 'settings' };
  if (pathname === '/crony') return { moduleId: 'crony' };
  if (pathname === '/helpdesk') return { moduleId: 'helpdesk' };
  if (pathname === '/affilmaster/socials/test') return { moduleId: 'socials-test' };
  if (pathname === '/affilmaster' || pathname === '/affilmaster/') return { moduleId: 'dashboard' };
  for (const mod of AFFILMASTER_MENU) {
    for (const child of mod.children ?? []) {
      const matchedPath = [child.path, child.legacyPath].find(
        (candidate) => candidate && (pathname === candidate || pathname.startsWith(candidate + '/')),
      );
      if (!matchedPath) continue;
      if (pathname === matchedPath) return { moduleId: mod.id, childId: child.id };
      const remainder = pathname.slice(matchedPath.length + 1);
      const detail = decodeURIComponent(child.id === 'am-vystupy-b2b' ? remainder : remainder.split('/')[0] ?? '');
      return { moduleId: mod.id, childId: child.id, detail: detail || undefined };
    }
  }
  // Unknown /affilmaster/* path → dashboard (deep links to unbuilt sections
  // still land inside the Affilmaster shell, not the Rondo one).
  return { moduleId: 'dashboard' };
}

// On the affilmaster SUBDOMAIN the host already selects the Space, so the URL
// path is clean (no '/affilmaster' prefix). On the legacy bare-host
// '/affilmaster/*' alias the prefix stays. `routeToPath`/`pathToRoute` work in
// the canonical '/affilmaster…' form; these adapt to the actual address bar.
function toAddressBar(canonicalPath: string, subdomainMode: boolean): string {
  if (canonicalPath === '/nastaveni') return '/nastaveni';
  if (canonicalPath === '/helpdesk') return '/helpdesk';
  if (canonicalPath === '/crony') return '/crony';
  if (!subdomainMode) return canonicalPath;
  const stripped = canonicalPath.replace(/^\/affilmaster/, '');
  return stripped === '' ? '/' : stripped;
}
function toCanonical(addressPath: string, subdomainMode: boolean): string {
  if (addressPath === '/nastaveni') return '/nastaveni';
  if (addressPath === '/helpdesk') return '/helpdesk';
  if (addressPath === '/crony') return '/crony';
  if (!subdomainMode) return addressPath;
  if (addressPath === '/' || addressPath === '') return '/affilmaster';
  return '/affilmaster' + addressPath;
}

export function AffilmasterShell() {
  setPagesSpace('affilmaster');
  const { me } = useAuth();
  const subdomainMode = me?.activeSpaceSlug === 'affilmaster';
  const [route, setRoute] = useState<Route>(() =>
    pathToRoute(toCanonical(window.location.pathname, subdomainMode)),
  );
  const readTaskId = (): number | null => {
    const raw = new URLSearchParams(window.location.search).get('taskId');
    return raw != null && raw !== '' && !Number.isNaN(Number(raw)) ? Number(raw) : null;
  };
  const [taskDetailId, setTaskDetailId] = useState<number | null>(readTaskId);
  const [esignHeader, setEsignHeader] = useState(DEFAULT_ESIGN_HEADER);

  useEffect(() => {
    const base = toAddressBar(routeToPath(route), subdomainMode);
    const q = route.childId === 'am-ukoly' && taskDetailId != null ? `?taskId=${taskDetailId}` : '';
    const target = base + q;
    if (target !== window.location.pathname + window.location.search) {
      window.history.pushState(null, '', target);
    }
  }, [route, subdomainMode, taskDetailId]);

  useEffect(() => {
    const onPop = () => {
      setRoute(pathToRoute(toCanonical(window.location.pathname, subdomainMode)));
      setTaskDetailId(readTaskId());
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [subdomainMode]);

  useScrollRestore(`${routeToPath(route)}|${taskDetailId ?? ''}`);

  if (!me) return null;

  const selectModule = (item: MenuItem) => {
    const first = item.children?.[0];
    if (first?.id !== 'am-ukoly') setTaskDetailId(null);
    setRoute(first ? { moduleId: item.id, childId: first.id } : { moduleId: item.id });
  };
  const selectChild = (item: MenuItem, child: SubItem) => {
    if (child.id !== 'am-ukoly') setTaskDetailId(null);
    setRoute({ moduleId: item.id, childId: child.id });
  };

  const module = AFFILMASTER_MENU.find((m) => m.id === route.moduleId) ?? null;
  const child = module?.children?.find((c) => c.id === route.childId) ?? null;
  const Page = route.childId ? BUILT_PAGES[route.childId] : undefined;
  const pageTitle =
    route.moduleId === 'dashboard'
      ? 'Dashboard'
      : module && child
        ? `${module.label}, ${child.label}`
        : (module?.label ?? '');
  const canSeeCrony = !!me.canAccessAllSpacesInTenant || !!me.permissionKeys?.includes('admin:users:manage');
  const allowedModuleKeys = computeAllowedModuleKeys(me);
  const canSeeHelpdesk = allowedModuleKeys === null || allowedModuleKeys.has('helpdesk');
  const canSeeCrm = allowedModuleKeys === null || allowedModuleKeys.has('crm');
  const arcRefresh = true;

  return (
    <PlatformShell
      menu={AFFILMASTER_MENU}
      currentSpaceSlug="affilmaster"
      activeModuleId={route.moduleId}
      activeChildId={route.childId}
      isTasksActive={route.childId === 'am-ukoly'}
      isHelpdeskActive={route.moduleId === 'helpdesk'}
      onSelectModule={selectModule}
      onSelectChild={selectChild}
      onOpenTasks={() => setRoute({ moduleId: 'provoz', childId: 'am-ukoly' })}
      onOpenTask={(taskId) => { setTaskDetailId(taskId); setRoute({ moduleId: 'provoz', childId: 'am-ukoly' }); }}
      onOpenHelpdesk={() => setRoute({ moduleId: 'helpdesk' })}
      onOpenSettings={() => setRoute({ moduleId: 'settings' })}
      onOpenCrony={() => setRoute({ moduleId: 'crony' })}
      showHelpdesk={canSeeHelpdesk}
      showCrony={canSeeCrony}
      pageTitle={undefined}
      arcRefresh={arcRefresh}
    >
      {route.moduleId === 'settings' ? (
        <SettingsPage />
      ) : route.moduleId === 'crony' ? (
        <CronyPage />
      ) : route.moduleId === 'helpdesk' ? (
        <HelpdeskPage />
      ) : route.moduleId === 'socials-test' ? (
        <AffilmasterSocialsTestPage />
      ) : route.moduleId === 'dashboard' ? (
        <AffilmasterDashboardPage />
      ) : route.childId === 'am-ukoly' ? (
        <TasksPage
          selectedTaskId={taskDetailId}
          onSelectTask={setTaskDetailId}
        />
      ) : route.childId === 'klienti-prehledy' ? (
        <AffilmasterKlientiPrehledyPage
          onOpenKlient={(slug) => setRoute({ moduleId: 'klienti', childId: 'klienti-seznam', detail: slug })}
        />
      ) : route.childId === 'klienti-seznam' ? (
        <AffilmasterKlientiPage
          detailSlug={route.detail}
          onOpenDetail={(slug) => setRoute((r) => ({ ...r, detail: slug }))}
          onOpenAffiliate={(id) => setRoute({ moduleId: 'affiliates', childId: 'affiliates-seznam', detail: id })}
          onOpenEsign={canSeeCrm ? (id) => setRoute({ moduleId: 'crm', childId: 'am-crm-esign', detail: String(id) }) : undefined}
          onCloseDetail={() => setRoute((r) => ({ moduleId: r.moduleId, childId: r.childId }))}
        />
      ) : route.childId === 'affiliates-seznam' ? (
        <AffilmasterAffiliatesPage
          detailId={route.detail}
          onOpenDetail={(id) => setRoute((r) => ({ ...r, detail: id }))}
          onCloseDetail={() => setRoute((r) => ({ moduleId: r.moduleId, childId: r.childId }))}
        />
      ) : route.childId === 'am-crm-leady' ? (
        <AffilmasterPage title="Leady" description="Přehled affiliate a inzerentských leadů" actionsSlotId={route.detail && route.detail !== 'novy' ? undefined : 'crm-leady-actions'}>
          <CrmLeadyPage
            actionsSlotId={route.detail && route.detail !== 'novy' ? undefined : 'crm-leady-actions'}
            onOpenAffiliate={(papId) => setRoute({ moduleId: 'affiliates', childId: 'affiliates-seznam', detail: papId })}
            onOpenClient={(name) => setRoute({ moduleId: 'klienti', childId: 'klienti-seznam', detail: klientSlug(name) })}
            detailId={route.detail}
            onOpenDetail={(id) => setRoute((r) => ({ ...r, detail: id }))}
            onCloseDetail={() => setRoute((r) => ({ moduleId: r.moduleId, childId: r.childId }))}
          />
        </AffilmasterPage>
      ) : route.childId === 'am-crm-pipeline' ? (
        <AffilmasterPage title="Pipeline" description="Obchodní fáze affiliate a inzerentských leadů">
          <CrmPipelinePage onOpenDetail={(id) => setRoute({ moduleId: 'crm', childId: 'am-crm-leady', detail: id })} />
        </AffilmasterPage>
      ) : route.childId === 'am-vystupy-promo' ? (
        <AffilmasterPage eyebrow="AFFILMASTER VÝSTUPY" title="Promo News" description="Týdenní promo akce klientů pro affiliate partnery" actionsSlotId="stranky-promo-actions">
          <Suspense fallback={<div className="p-4 text-muted">Načítám…</div>}>
            <StrankyPromoPage detail={route.detail} onOpenDetail={(d) => setRoute((r) => ({ moduleId: r.moduleId, childId: r.childId, detail: d }))} />
          </Suspense>
        </AffilmasterPage>
      ) : route.childId === 'am-vystupy-podpora' ? (
        <AffilmasterPage eyebrow="AFFILMASTER VÝSTUPY" title="Podpora" description="Obsah webu podpory ve třech jazycích" actionsSlotId="stranky-podpora-actions">
          <Suspense fallback={<div className="p-4 text-muted">Načítám…</div>}>
            <StrankyPodporaPage detail={route.detail} onOpenDetail={(d) => setRoute((r) => ({ moduleId: r.moduleId, childId: r.childId, detail: d }))} />
          </Suspense>
        </AffilmasterPage>
      ) : route.childId === 'am-vystupy-b2b' ? (
        <AffilmasterPage eyebrow="AFFILMASTER VÝSTUPY" title="B2B" description="Nabídky a reporty pro klienty, sdílené odkazem bez hesla">
          <Suspense fallback={<div className="p-4 text-muted">Načítám…</div>}>
            <StrankyB2BPage tab={route.detail} onOpenTab={(d) => setRoute((r) => ({ moduleId: r.moduleId, childId: r.childId, detail: d }))} />
          </Suspense>
        </AffilmasterPage>
      ) : route.childId === 'am-vystupy-vizitky' ? (
        <AffilmasterPage eyebrow="AFFILMASTER VÝSTUPY" title="Vizitky" description="Digitální vizitky členů týmu">
          <Suspense fallback={<div className="p-4 text-muted">Načítám…</div>}>
            <StrankyVizitkyPage />
          </Suspense>
        </AffilmasterPage>
      ) : route.childId === 'am-crm-esign' ? (
        <AffilmasterPage title={esignHeader.title} description={esignHeader.description} actionsSlotId={route.detail ? undefined : 'crm-esign-actions'}>
          <Suspense fallback={<div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /><span /></div>}>
            <EsignPage
              actionsSlotId={route.detail ? undefined : 'crm-esign-actions'}
              detailId={route.detail}
              onHeaderChange={setEsignHeader}
              onOpenDetail={(id) => setRoute((r) => ({ ...r, detail: id }))}
              onCloseDetail={() => setRoute((r) => ({ moduleId: r.moduleId, childId: r.childId }))}
              onOpenClient={(name) => setRoute({ moduleId: 'klienti', childId: 'klienti-seznam', detail: klientSlug(name) })}
            />
          </Suspense>
        </AffilmasterPage>
      ) : route.childId === 'am-vyuc-partneri' ? (
        <VyuctovaniKlientiPage
          config={AM_VYUCTOVANI}
          detailId={route.detail}
          onOpenDetail={(id) => setRoute((r) => ({ ...r, detail: id }))}
          onCloseDetail={() => setRoute((r) => ({ moduleId: r.moduleId, childId: r.childId }))}
          onOpenClient={(name) => { hintKlientSection('dealy'); setRoute({ moduleId: 'klienti', childId: 'klienti-seznam', detail: klientSlug(name) }); }}
        />
      ) : route.childId === 'am-crm-formular' ? (
        <AffilmasterPage title="Formulář" description="Nastavení veřejného registračního formuláře">
          <CrmFormularPage hideHeading />
        </AffilmasterPage>
      ) : Page ? (
        <Page />
      ) : (
        <ComingSoon label={pageTitle} />
      )}
    </PlatformShell>
  );
}

function AffilmasterPage({ title, description, actionsSlotId, eyebrow = 'AFFILMASTER CRM', children }: { title: string; description: string; actionsSlotId?: string; eyebrow?: string; children: JSX.Element }) {
  return <div className="am-crm-wrap"><div className="prototype-page-head"><div><div className="prototype-eyebrow">{eyebrow}</div><h1>{title}</h1><p>{description}</p></div>{actionsSlotId ? <div id={actionsSlotId} className="crm-head-actions-slot" /> : null}</div>{children}</div>;
}

function ComingSoon({ label }: { label: string }) {
  return (
    <section className="prototype-coming-soon">
      <div className="prototype-page-head"><div><div className="prototype-eyebrow">AFFILMASTER</div><h1>{label}</h1></div></div>
      <div className="prototype-panel prototype-empty-state"><div className="prototype-empty-icon">◇</div><h2>Tato stránka se připravuje</h2><p>Obsah bude dostupný po zapojení této části Affilmasteru.</p></div>
    </section>
  );
}
